import { z } from "zod";
import { ISODateSchema, ULIDSchema } from "./primitives.js";
import { UserIdSchema } from "./user.js";
import { ShoppingCategorySchema, DEFAULT_CATEGORY } from "../categories.js";
export const ShoppingListStatusSchema = z.enum(["active", "archived"]);
export const ShoppingListItemSchema = z
    .object({
    id: ULIDSchema("item"),
    name: z.string().min(1).max(200),
    qty: z.number().nonnegative().nullable(),
    unit: z.string().max(32).nullable(),
    category: ShoppingCategorySchema.default(DEFAULT_CATEGORY),
    checked: z.boolean().default(false),
    mergeable: z.boolean().default(true),
    sourceRecipeIds: z.array(ULIDSchema("rec")).default([]),
    addedManually: z.boolean().default(false),
})
    .passthrough();
export const GeneratedFromSchema = z
    .object({
    recipeIds: z.array(ULIDSchema("rec")),
    scaling: z.record(z.string(), z.number().positive()),
})
    .passthrough()
    .nullable();
const listCore = {
    schemaVersion: z.literal(1),
    name: z.string().min(1).max(120),
    status: ShoppingListStatusSchema.default("active"),
    items: z.array(ShoppingListItemSchema).default([]),
    generatedFrom: GeneratedFromSchema.default(null),
    createdAt: ISODateSchema,
    updatedAt: ISODateSchema,
    createdBy: UserIdSchema,
    updatedBy: UserIdSchema,
    deletedAt: ISODateSchema.nullable(),
};
export const ShoppingListRowSchema = z
    .object({ householdId: ULIDSchema("hh"), sk: ULIDSchema("list"), id: ULIDSchema("list"), ...listCore })
    .passthrough();
export const ShoppingListApiItemSchema = z
    .object({ id: ULIDSchema("list"), ...listCore })
    .passthrough();
export function shoppingListRowToApiItem(raw) {
    const row = ShoppingListRowSchema.parse(raw);
    const merged = { ...row };
    delete merged["householdId"];
    delete merged["sk"];
    delete merged["ttl"];
    return ShoppingListApiItemSchema.parse(merged);
}
export const ManualItemInputSchema = z.object({
    name: z.string().min(1).max(200),
    qty: z.number().nonnegative().nullable().optional(),
    unit: z.string().max(32).nullable().optional(),
    category: ShoppingCategorySchema.optional(),
}).strict();
export const CreateShoppingListRequestSchema = z
    .object({
    name: z.string().min(1).max(120),
    recipeIds: z.array(ULIDSchema("rec")).max(50).optional(),
    // `scaleByServings[recipeId]` is a MULTIPLIER on the recipe's quantities (1 = as written,
    // 2 = double). The frontend computes target-servings scaling as target/recipe.servings.
    // Bounded to ≤ 50 keys (matches recipeIds cap) to prevent a cardinality-DoS.
    scaleByServings: z
        .record(z.string(), z.number().positive())
        .refine((r) => Object.keys(r).length <= 50, "≤ 50 scale entries")
        .optional(),
    items: z.array(ManualItemInputSchema).max(200).optional(),
})
    .strict();
export const PatchShoppingListRequestSchema = z
    .object({ name: z.string().min(1).max(120).optional(), status: ShoppingListStatusSchema.optional() })
    .strict()
    .refine((v) => v.name !== undefined || v.status !== undefined, "at least one of name|status is required");
// Add an item: EITHER manual fields OR fromRecipeId/fromRecipeIds (mutually exclusive in practice).
export const AddShoppingListItemRequestSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    qty: z.number().nonnegative().nullable().optional(),
    unit: z.string().max(32).nullable().optional(),
    category: ShoppingCategorySchema.optional(),
    // Plural recipe merge (meal-plan "build shopping list" path). Mirrors CreateShoppingListRequestSchema.
    fromRecipeIds: z.array(ULIDSchema("rec")).max(50).optional(),
    // Legacy single-recipe add (kept for back-compat). Prefer fromRecipeIds.
    fromRecipeId: ULIDSchema("rec").optional(),
    // Union: scalar for the legacy singular path, record (keyed by recipeId) for the plural path.
    scaleByServings: z
        .union([z.number().positive(), z.record(z.string(), z.number().positive())])
        .optional(),
})
    .strict()
    .refine((v) => v.name !== undefined || v.fromRecipeId !== undefined || (v.fromRecipeIds?.length ?? 0) > 0, "name, fromRecipeId, or fromRecipeIds required");
export const PatchShoppingListItemRequestSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    qty: z.number().nonnegative().nullable().optional(),
    unit: z.string().max(32).nullable().optional(),
    category: ShoppingCategorySchema.optional(),
    checked: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, "at least one field required");
export const ShoppingListResponseSchema = z.object({ list: ShoppingListApiItemSchema });
export const ShoppingListsResponseSchema = z.object({
    items: z.array(ShoppingListApiItemSchema),
    nextCursor: z.string().nullable().optional(),
});
//# sourceMappingURL=shopping-list.js.map